import { useEffect, useMemo } from "react";
import { toast } from "sonner";
import type { Document } from "@/types/documents";
import { useTemplateFormDocuments } from "./useTemplateFormDocuments";

/**
 * The filenames a template form seeds its document number from.
 *
 * The whole project's documents are fetched when a form opens — the page's
 * filtered list would seed a colliding number — and the page's list only
 * stands in (`fallback`) when there is no project to fetch from. While the
 * fetch is loading or has failed the list stays empty rather than wrong; the
 * number corrects itself once data lands, and the save reserves the real one
 * through `next_template_form_number` regardless.
 *
 * The attachment picker no longer reads this: it is the shared DocumentPicker
 * now, which fetches and pages the project itself.
 */
export function useAttachableDocuments(
  projectId: string | null,
  fallback: Document[],
  active: boolean,
  errorMessage: string
): { documentNames: string[] } {
  const scoped = useTemplateFormDocuments(projectId, active);
  const scopedData = scoped.data;
  const inScope = useMemo(
    () => (projectId === null ? fallback : (scopedData ?? [])),
    [projectId, fallback, scopedData]
  );

  const { isError, errorUpdatedAt } = scoped;
  useEffect(() => {
    // No retry action on the toast: while the form (a modal Radix dialog) is
    // open, DismissableLayer sets pointer-events: none on the body and sonner
    // toasts stay unclickable.
    if (isError) {
      toast.error(errorMessage);
    }
  }, [isError, errorUpdatedAt, errorMessage]);

  return { documentNames: useMemo(() => inScope.map((doc) => doc.filename), [inScope]) };
}
